import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Stack, Text } from '@mantine/core';
import { IconArrowBackUp } from '@tabler/icons-react';
import { UseMutationResult, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TravelReturnModeSelectControl } from '@/module/Travel/Settings/components/form/TravelReturnModeSelectControl';
import { getTravelSettingsOptions } from '@/module/Travel/Settings/model/TravelSettingsQuery';
import { TravelReturnMode } from '@/module/Travel/Settings/model/TravelSettingsTypes';

import { Form, PendingSpinner, SwitchControl, TextareaControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { TravelReturnSchema, toTravelReturnRequest, travelReturnSchema } from '../model/TravelApprovalSchema';
import { TravelReturnRequest } from '../model/TravelApprovalTypes';

interface TravelApprovalReturnModalProps {
  title: string;
  content: string;
  mutation: UseMutationResult<unknown, Error, TravelReturnRequest>;
  onClose: () => void;
}

interface TravelApprovalReturnFormProps extends Omit<TravelApprovalReturnModalProps, 'title'> {
  defaultReturnMode: TravelReturnMode | null;
}

export function TravelApprovalReturnModal({ title, content, mutation, onClose }: TravelApprovalReturnModalProps) {
  const { data: settings, isPending } = useQuery(getTravelSettingsOptions());

  return (
    <Modal
      opened
      centered
      onClose={onClose}
      title={title}
      size="lg"
    >
      {isPending ? (
        <PendingSpinner h="120px" />
      ) : (
        <TravelApprovalReturnForm
          content={content}
          // Null when the settings could not be read, so the approver picks a mode instead of trusting a default nobody sent.
          defaultReturnMode={settings?.returnMode ?? null}
          mutation={mutation}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

function TravelApprovalReturnForm({ content, defaultReturnMode, mutation, onClose }: TravelApprovalReturnFormProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();

  const form = useForm<TravelReturnSchema>({
    resolver: zodResolver(travelReturnSchema),
    defaultValues: {
      reason: '',
      returnMode: defaultReturnMode,
      notifyOtherApprovers: defaultReturnMode === 'STRAIGHT_BACK',
    },
  });

  const { setValue } = form;
  const canNotify = form.watch('returnMode') === 'STRAIGHT_BACK';

  // Only a straight-back return leaves approvers who keep their approval, so the switch follows the mode rather than promising a mail nobody gets.
  useEffect(() => {
    setValue('notifyOtherApprovers', canNotify);
  }, [canNotify, setValue]);

  // Two clicks in one frame both see isPending false, so the loading button alone lets the second one through.
  const inFlight = useRef(false);

  const handleSubmit = (values: TravelReturnSchema) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    mutation.mutate(toTravelReturnRequest(values), {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        onClose();
      },
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <Form
      onSubmit={form.handleSubmit(handleSubmit)}
      onCancel={onClose}
      isSubmitting={mutation.isPending}
      submitLabel={t('travel.approvals.actions.return')}
      submitColor="yellow.4"
      submitIcon={<IconArrowBackUp />}
    >
      <Stack gap="md">
        <Text size="sm">{content}</Text>
        <TravelReturnModeSelectControl
          name="returnMode"
          label={t('travel.approvals.actions.returnMode')}
          isSubmitting={mutation.isPending}
          withAsterisk
          control={form.control}
        />
        <SwitchControl
          name="notifyOtherApprovers"
          label={t('travel.approvals.actions.notifyOtherApprovers')}
          description={t('travel.approvals.actions.notifyOtherApproversHint')}
          isSubmitting={mutation.isPending}
          isDisabled={!canNotify}
          control={form.control}
        />
        <TextareaControl
          name="reason"
          label={t('travel.approvals.actions.reason')}
          isSubmitting={mutation.isPending}
          withAsterisk
          control={form.control}
        />
      </Stack>
    </Form>
  );
}
